import { z } from 'zod';

/**
 * Contrats des invitations à jouer (spec 10, ADR 0019) — partie privée → email.
 * Quotas EN COUCHES (spec 10 §7), autorité serveur : ces constantes sont la
 * source de vérité partagée (le front les affiche, l'API les applique).
 * ⚠️ Aucun DTO n'expose l'état d'opt-out/suppression d'un destinataire
 * (anti-énumération, spec 10 §10) : `sent` = « invitation enregistrée »,
 * jamais « email parti ».
 */

/** Cap par PARTIE : nombre total d'invitations sur un même match. */
export const INVITES_PER_MATCH_MAX = 15;
/** Quota ÉMETTEUR : invitations créées par heure (tous types confondus). */
export const INVITES_SENDER_HOURLY = 10;
/** Quota ÉMETTEUR : invitations créées par 24 h (tous types confondus). */
export const INVITES_SENDER_DAILY = 30;
/** Quota ÉMETTEUR : invitations vers des emails EXTERNES par 24 h. */
export const INVITES_SENDER_EXTERNAL_DAILY = 10;
/** DESTINATAIRE compte : délai minimal entre 2 emails d'invitation reçus. */
export const INVITES_RECIPIENT_COOLDOWN_MS = 5 * 60_000;
/** DESTINATAIRE compte : emails d'invitation reçus max par 24 h. */
export const INVITES_RECIPIENT_DAILY = 10;
/** DESTINATAIRE externe : délai minimal entre 2 emails (tous émetteurs). */
export const INVITES_EXTERNAL_COOLDOWN_MS = 15 * 60_000;
/** DESTINATAIRE externe : emails max par 24 h et par adresse. */
export const INVITES_EXTERNAL_DAILY = 5;
/** Plafond de cibles (amis + emails) par appel API. */
export const INVITES_PER_CALL_MAX = 10;

/**
 * Normalisation UNIQUE des emails d'invitation (spec 10 §2/§10) : trim +
 * lowercase — partagée entre le schéma d'entrée, la dédup, les quotas
 * destinataire et la liste de suppression.
 */
export function normalizeInviteEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Corps de `POST /api/games/:game/matches/:idOrCode/invitations` (spec 10 §4) :
 * amis (cuid) et/ou emails externes, 1 à INVITES_PER_CALL_MAX cibles au total.
 */
export const CreateInvitationsSchema = z
  .object({
    friendIds: z.array(z.string().cuid()).optional(),
    emails: z.array(z.string().trim().toLowerCase().max(254).email()).optional(),
  })
  .strict()
  .refine(
    (v) => {
      const total = (v.friendIds?.length ?? 0) + (v.emails?.length ?? 0);
      return total >= 1 && total <= INVITES_PER_CALL_MAX;
    },
    { message: `entre 1 et ${INVITES_PER_CALL_MAX} cibles par appel` },
  );
export type CreateInvitationsInput = z.infer<typeof CreateInvitationsSchema>;

/** Une cible d'invitation : un compte ami OU une adresse externe. */
export type InvitationTargetDto = { userId: string } | { email: string };

/**
 * Résultat par cible (spec 10 §4). `sent` = invitation ENREGISTRÉE (jamais
 * d'info sur l'envoi effectif — vie privée du destinataire, spec 10 §5).
 */
export interface InvitationResultDto {
  target: InvitationTargetDto;
  status: 'sent' | 'already_invited';
}

/**
 * Une invitation que J'AI émise sur une partie (état « Invité ✓ » du lobby).
 * `email` = adresse que J'AI saisie via `emails` (spec 10 §4) — externe OU
 * adresse-de-compte : le serveur reflète la SAISIE et ne renvoie JAMAIS
 * `userId`/`displayName` pour ces lignes (anti-désanonymisation, audit
 * 2026-07-12). `userId`/`displayName` = cible invitée par `friendIds`.
 */
export interface SentInvitationDto {
  userId?: string;
  displayName?: string;
  email?: string;
  at: string;
}
